<script>
  import Input from "../input-design/Input.svelte";
  import Editor from "../input-design/Editor.svelte";
  import SocialNetwork from "../social-network/SocialNetwork.svelte";

  import {
    projectDetailStore,
    initialProjectDetail,
  } from "../../../stores/projectStore";
  import { createEventDispatcher, getContext } from "svelte";
  import { replace } from "svelte-spa-router";
  import { isLoaderActive } from "../../../store";
  import { validateField } from "../../../utils";

  const dispatch = createEventDispatcher();

  let fields = initialProjectDetail();
  let localImageFile, localRewardDistributionDate, localRewardDistributionTime ;
  let errors = initialProjectDetail();

  let disableNext = false;

  const isValidURL = (string) => {
    string = string.toLowerCase();
    let validateUrl = string.match(
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
    );
    return validateUrl !== null;
  };
  const saveProject = getContext("save-project");

  const populateRewardDistributionDate = () => {
    fields.rewardDistributionDate = new Date(
            `${localRewardDistributionDate}T${localRewardDistributionTime}:00.000Z`
    ).getTime();
  }

  const saveFormInDraft = async () => {
    if ($isLoaderActive) return;
    isLoaderActive.set(true);
    populateRewardDistributionDate();
    await saveProject(fields);
    isLoaderActive.set(false);
    replace("/sale-listing");
  };

  projectDetailStore.subscribe((value) => {
    fields = { ...value };
    if (fields.coverImage)
      localImageFile =
        fields.coverImage instanceof File
          ? URL.createObjectURL(fields.coverImage)
          : fields.coverImage.url;
    if (fields.rewardDistributionDate) {
      let date = new Date(fields.rewardDistributionDate).toISOString();
        localRewardDistributionDate = date.substring(0, 10);
        localRewardDistributionTime = date.substring(11, 16);
    }
  })();

  const validateProjectDetailForm = () => {
    let valid = true;
    window.scroll({ top: 0 });

    errors.projectTitle = validateField(fields.projectTitle, "Project title");
    errors.shortDescription = validateField(
      fields.shortDescription,
      "Short description"
    );

  fields.rewardDistributionDate = new Date(
      `${localRewardDistributionDate}T${localRewardDistributionTime}:00.000Z`
    ).getTime();

    if (!fields.coverImage) {
      errors.coverImage = "Cover image is required";
    }
    errors.story = validateField(fields.story, "Story");

    valid = !(
      errors.projectTitle ||
      errors.shortDescription ||
      errors.coverImage ||
      errors.story ||
      validateSocial()
    );

    if ( fields.rewards.length != 0 && (!localRewardDistributionDate || !localRewardDistributionTime )) {
      valid = false;
      errors.rewardDistributionDate = "Select Date and time";
    }

    if (localRewardDistributionDate && localRewardDistributionTime) {
      let currentDate = new Date();
      populateRewardDistributionDate();
      if (currentDate.getTime() > fields.rewardDistributionDate) {
        valid = false;
        errors.rewardDistributionDate = "Estimated delivery time should be greater than now";
      }
    }

    if (!valid) return window.scroll({ top: 0 });

    errors = initialProjectDetail();
    projectDetailStore.setProjectDetail({ ...fields });
    dispatch("next");
    return true;
  };

  const validateSocial = () => {
    let valid = false;

    const evaluateError = (field, message) => {
      if (!field.trim()) return null;
      let error = validateField(field, message, (field) => !isValidURL(field));
      if (!error) return null;
      return error;
    };
    errors.mediaUrls.facebook = evaluateError(fields.mediaUrls.facebook, "Facebook url") ?? "";
    errors.mediaUrls.twitter = evaluateError(fields.mediaUrls.twitter, "Twitter url") ?? "";
    errors.mediaUrls.linkedin = evaluateError(fields.mediaUrls.linkedin, "Linkedin url") ?? "";
    errors.mediaUrls.instagram = evaluateError(fields.mediaUrls.instagram, "Instagram url") ?? "";
    errors.mediaUrls.telegram = evaluateError(fields.mediaUrls.telegram, "Telegram url") ?? "";
    errors.mediaUrls.youtube = evaluateError(fields.mediaUrls.youtube, "Youtube url") ?? "";
    errors.mediaUrls.twitch = evaluateError(fields.mediaUrls.twitch, "Twitch url") ?? "";
    errors.mediaUrls.discord = evaluateError(fields.mediaUrls.discord, "Discord url") ?? "";
  
    valid =
      errors.mediaUrls.facebook ||
      errors.mediaUrls.twitter ||
      errors.mediaUrls.linkedin ||
      errors.mediaUrls.instagram ||
      errors.mediaUrls.telegram ||
      errors.mediaUrls.youtube ||
      errors.mediaUrls.twitch ||
      errors.mediaUrls.discord;

    return valid;
  };

  const setImageValues = async (file) => {
    let img = new Image();
    img.src = window.URL.createObjectURL(file);
    img.onload = () => {
      if (img.width >= 720 && img.height >= 470) {
        errors.coverImage = "";
        localImageFile = URL.createObjectURL(file);
        fields.coverImage = file;
      } else {
        errors.coverImage =
          "image should be greater then or equal to (720 x 470)";
      }
    };
  };

  const clearImage = () => {
    localImageFile = fields.coverImage = null;
  };
</script>

<section>
  <form>
    <div class="flex flex-wrap form-row items-start">
      <div class="col-12">
        <Input
          bind:value={fields.projectTitle}
          on:input={() => (errors = { ...errors, projectTitle: "" })}
          error={errors.projectTitle}
          placeholder="Enter project title"
          id="project-title"
        >
          <label for="project-title" slot="label" class="form-label">
            Project Title
            <small>*</small>
          </label>
          <span slot="label_dec" class="form-label-dec">
            What is the name of your project?
          </span>
        </Input>
        <div class="error">{errors.projectTitle}</div>
      </div>
      <div class="col-12">
        <Input
          bind:value={fields.shortDescription}
          on:input={() => (errors = { ...errors, shortDescription: "" })}
          error={errors.shortDescription}
          placeholder="Enter short description"
          id="short-des"
        >
          <label for="short-des" slot="label" class="form-label">
            Short Description
            <small>*</small>
          </label>
          <span slot="label_dec" class="form-label-dec">
            Describe your project in 120 words.
          </span>
        </Input>
        <div class="error">{errors.shortDescription}</div>
      </div>
      <div class="col-6">
        <label for="cover-image" class="form-label">
          Cover Image
          <small>*</small>
        </label>
        <span class="form-label-dec">Chose the front cover of your project.</span>
        <div class="relative">
          {#if localImageFile}
            <img
              on:click={clearImage}
              class="remove-image"
              src="./images/cross.svg"
              alt=""
            />
          {/if}
          <label
            for="project-image"
            class="cursor-pointer relative flex justify-center items-center
          project-image-wrap"
          >
            <input
              type="file"
              accept="image/*"
              class="absolute"
              id="project-image"
              on:input={() => (errors = { ...errors, coverImage: "" })}
              on:change={(e) => setImageValues(e.target.files[0])}
            />
            {#if localImageFile}
              <!-- svelte-ignore a11y-img-redundant-alt -->
              <img
                src={localImageFile}
                class="project-image"
                alt="project image"
              />
            {/if}
            <span class="flex column-direction items-center">
              <!-- svelte-ignore a11y-img-redundant-alt -->
              <img
                src="./images/file-upload.svg"
                class="m-auto"
                alt="upload image"
              />
              Click to add an asset or drag and
              <br />
              drop one in this area
              <br />
              <br />
              Image size should be (720 x 470)
            </span>
          </label>
        </div>
        <div class="error">{errors.coverImage}</div>
      </div>
      <div class="col-12">
        <label for="" class="form-label">Social Media URL’s</label>
        <span class="form-label-dec">Social Media helps to be more visibility to your project and your community.</span>
        <SocialNetwork mediaUrls={fields.mediaUrls} errors={errors.mediaUrls} />
      </div>
      <div class="col-12">
        <label for="" class="form-label">
          Story
          <small>*</small>
        </label>
        <span class="form-label-dec">Describe your project in detail to engage and capture your audience.</span>
        <div id="story-editor">
          <Editor
            initialValue={fields.story}
            on:change={(event) => {
              fields.story = event.detail["value"];
            }}
            on:upload={(event) => {
                disableNext = event.detail
            }}
          />
        </div>
        <div class="error">{errors.story}</div>
      </div>
      <div class="col-12">
        <label for="reward-editor" class="form-label">Rewards</label>
        <span class="form-label-dec">
            Define what perks come with your Rewards.
          </span>
        <div id="reward-editor">
          <Editor
            initialValue={fields.rewards}
            on:change={(event) => {
              fields.rewards = event.detail["value"];
            }}
            on:upload={(event) => {
              disableNext = event.detail
            }}
          />
        </div>
      </div>
      <div class="col-12">
        <div class="sub-row">
          <div class="col-8">
            <Input
                    type="date"
                    bind:value={localRewardDistributionDate}
                    on:input={() => (errors = { ...errors, rewardDistributionDate: "" })}
                    placeholder="Enter project title"
                    id="reward-estimation-date"
            >
              <label for="reward-estimation-date"  slot="label" class="form-label">Rewards Estimated Delivery</label>
              <span class="form-label-dec" slot="label_dec"> Set estimated date time for rewards distribution. </span>
            </Input>
          </div>
          <div class="col-4 flex items-end">
            <Input
                    type="time"
                    bind:value={localRewardDistributionTime}
                    on:input={() => (errors = { ...errors, rewardDistributionDate: "" })}
                    max="9999-12-31"
                    placeholder="Enter project title"
                    id="reward-estimation-time"
            >
            </Input>
          </div>
          <div class="error">{errors.rewardDistributionDate}</div>
        </div>
      </div>
    </div>
  </form>
</section>
<div class="pl-button-wrapper">
  <button class="pl-button pl-save-btn" id="project-detail-save" on:click={saveFormInDraft}>Save</button>
  <button class={`pl-button pl-next-btn ${disableNext ? "disabled" : ""}`} id="project-detail-next" on:click={validateProjectDetailForm}>
    Next
    {#if disableNext}
      <img class="btn-load" src="./images/btn-load.gif" alt="loader img" />
    {/if}
  </button>
</div>
